import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-36 sm:pt-44 pb-20 sm:pb-28 overflow-hidden"
    >
      {/* Decorative warm wash */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-0 right-0 w-[60%] h-[80%] bg-secondary/30 blur-[120px] rounded-full" />
        <div className="absolute -bottom-32 -left-32 w-[40%] h-[60%] bg-primary/15 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left — text */}
        <div className="lg:col-span-7 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="inline-flex items-center gap-2 mb-7 px-4 py-2 rounded-full bg-card border border-border/60"
          >
            <MapPin
              size={14}
              strokeWidth={1.5}
              className="text-primary"
            />
            <span className="text-[11px] tracking-[0.22em] uppercase font-body font-medium text-foreground/70">
              Ψυχοθεραπεία · Πάτρα
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-headline"
            className="font-heading font-medium tracking-tight leading-[1.05] text-foreground text-[40px] sm:text-6xl lg:text-[68px]"
          >
            Άννα <span className="italic font-light">Παπαχρονοπούλου</span>
            <span className="block mt-3 text-[0.44em] sm:text-[0.4em] text-foreground/70 font-light tracking-normal">
              Γνωσιακή Συμπεριφορική Ψυχοθεραπεία · με επίκεντρο τον άνθρωπο
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            data-testid="hero-subheadline"
            className="mt-8 max-w-[540px] text-lg sm:text-xl font-body font-light leading-relaxed text-foreground/75"
          >
            Υποστήριξη σε άγχος, σχέσεις, αυτοεκτίμηση,
            ιδεοψυχαναγκαστική διαταραχή (OCD) και συναισθηματική δυσφορία.
            Ένας ασφαλής χώρος για να κατανοήσεις και να φροντίσεις τον εαυτό σου.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
          >
            <button
              onClick={() => scrollTo("contact")}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-background px-8 py-4 font-body text-base font-medium transition-all duration-300 hover:-translate-y-0.5 hover:bg-foreground/85 hover:shadow-[0_12px_40px_rgb(42,54,46,0.18)]"
            >
              Κλείσε Συνεδρία
              <ArrowRight
                size={18}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <button
              onClick={() => scrollTo("approach")}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent border border-border text-foreground px-8 py-4 font-body text-base font-medium transition-all duration-300 hover:bg-card hover:-translate-y-0.5"
            >
              Η προσέγγιση
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-14 flex items-center gap-6 text-xs sm:text-sm font-body text-muted-foreground"
          >
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Γνωσιακή Συμπεριφορική (CBT)</span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span>Δια ζώσης &amp; Online</span>
            </div>
          </motion.div>
        </div>

        {/* Right — arched image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[3/4] w-full max-w-[480px] mx-auto">
            <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-primary/20 blur-2xl" />
            <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-secondary/40 blur-2xl" />
            <div className="relative h-full w-full arch-mask overflow-hidden border border-border/40">
              <img
                src="https://customer-assets.emergentagent.com/job_therapy-anna-gr/artifacts/nx9midrs_IMG_7568.jpeg"
                alt="Γραφείο της Άννας Παπαχρονόπουλου, ψυχολόγου στην Πάτρα — ζεστός χώρος ψυχοθεραπείας"
                data-testid="hero-image"
                fetchPriority="high"
                width="480"
                height="640"
                className="h-full w-full object-cover"
              />
            </div>
            {/* Floating tag */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="absolute -left-4 sm:-left-10 bottom-12 bg-card/95 backdrop-blur-md border border-border/60 rounded-2xl px-5 py-4 shadow-[0_12px_40px_rgb(0,0,0,0.06)]"
            >
              <p className="font-heading italic text-foreground text-lg leading-tight">
                psyloveyourself
              </p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1">
                Αγάπη &amp; αποδοχή
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
